import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { QuiesceReason, QuiesceReasonCode } from '../../api/types';

interface CreateReasonFormProps {
  templateReasons: QuiesceReason[];
  copyFromReasonId: number | '';
  onCopyFrom: (id: number | '') => void;
  selectedTemplate: QuiesceReason | null;
  copiedReasonCodeInapplicable: boolean;
  applicableReasonCodes: QuiesceReasonCode[];
  reasonCode: string;
  onReasonCodeChange: (code: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  compensatingControls: string;
  onCompensatingControlsChange: (value: string) => void;
  durationDaysInput: string;
  onDurationDaysInputChange: (value: string) => void;
  maxDurationDays: number;
  submitting: boolean;
}

/** The "create a new reason" form — category, optional "copy details from"
 * prefill picker, reason code, description, compensating controls, and
 * duration. Extracted from `QuiesceDialog` to keep that component focused
 * on orchestration rather than also owning this much markup. */
export const CreateReasonForm = ({
  templateReasons,
  copyFromReasonId,
  onCopyFrom,
  selectedTemplate,
  copiedReasonCodeInapplicable,
  applicableReasonCodes,
  reasonCode,
  onReasonCodeChange,
  description,
  onDescriptionChange,
  compensatingControls,
  onCompensatingControlsChange,
  durationDaysInput,
  onDurationDaysInputChange,
  maxDurationDays,
  submitting,
}: CreateReasonFormProps) => (
  <>
    <FormControl fullWidth margin="normal" variant="outlined">
      <InputLabel>Category</InputLabel>
      <Select
        value="cant_fix"
        label="Category"
        disabled={submitting}
        onChange={() => {}}
      >
        <MenuItem value="cant_fix">Can't Fix (technical blocker)</MenuItem>
        <Tooltip title="Requires the approval & documentation process — coming soon">
          <span>
            <MenuItem value="wont_fix" disabled>
              Won't Fix (business decision)
            </MenuItem>
          </span>
        </Tooltip>
      </Select>
    </FormControl>
    {templateReasons.length > 0 && (
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Copy details from</InputLabel>
        <Select
          value={copyFromReasonId}
          onChange={e => onCopyFrom(e.target.value as number | '')}
          label="Copy details from"
          disabled={submitting}
        >
          <MenuItem value="">
            <em>Start from scratch</em>
          </MenuItem>
          {templateReasons.map(r => (
            <MenuItem key={r.id} value={r.id}>
              {r.cve_or_rule} — {r.description} (expires{' '}
              {r.expires_at.slice(0, 10)})
            </MenuItem>
          ))}
        </Select>
        {selectedTemplate && (
          <FormHelperText>
            Copies the description, compensating controls, and reason below
            from this reason (expires {selectedTemplate.expires_at.slice(0, 10)}
            ) — this still creates a separate, new reason for this issue with
            its own expiration; set the duration below.
          </FormHelperText>
        )}
      </FormControl>
    )}
    {copiedReasonCodeInapplicable && (
      <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
        The copied reason's code doesn't apply to this issue — choose a
        different Reason below.
      </Typography>
    )}
    <FormControl fullWidth margin="normal" variant="outlined">
      <InputLabel>Reason</InputLabel>
      <Select
        value={reasonCode}
        onChange={e => onReasonCodeChange(e.target.value as string)}
        label="Reason"
        disabled={submitting}
      >
        {applicableReasonCodes.map(rc => (
          <MenuItem key={rc.code} value={rc.code} disabled={rc.deprecated}>
            {rc.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <TextField
      fullWidth
      required
      multiline
      minRows={3}
      margin="normal"
      variant="outlined"
      label="Description"
      placeholder="Why is this finding being suppressed?"
      helperText="Include a tracking ticket, e.g. internal JIRA ticket, upstream vendor support case ticket/link, or other supporting reasons."
      value={description}
      onChange={e => onDescriptionChange(e.target.value)}
      disabled={submitting}
    />
    <TextField
      fullWidth
      required
      multiline
      minRows={2}
      margin="normal"
      variant="outlined"
      label="Compensating controls"
      placeholder="What keeps this issue non-exploitable while quiesced?"
      helperText="Required — describe the mitigations in place (e.g. network isolation, WAF rule, scanning cadence)."
      value={compensatingControls}
      onChange={e => onCompensatingControlsChange(e.target.value)}
      disabled={submitting}
    />
    <TextField
      fullWidth
      margin="normal"
      variant="outlined"
      type="number"
      label="Duration (days)"
      helperText={`Maximum ${maxDurationDays} days`}
      value={durationDaysInput}
      onChange={e => {
        // Keep the raw string in state so clearing the field shows empty
        // (not "0") and typing doesn't append after a stray leading zero —
        // Number(durationDaysInput) is only computed on submit.
        const raw = e.target.value;
        if (raw === '' || /^\d+$/.test(raw)) {
          onDurationDaysInputChange(raw);
        }
      }}
      inputProps={{ min: 1, max: maxDurationDays }}
      disabled={submitting}
    />
  </>
);
